import dayjs from "dayjs";
import { useTransition } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  DrawerType,
  editFormDataAF,
  getRidesMinFirstMaxLastDate,
  hookFormContextAF,
  IExtendableDto,
  IRideDto,
  RideCrudModeKey,
  RideCrudModeKeyTuple,
  ridesJourneyAF,
  useDrawers,
  useTimezone,
} from "@ryde/shared";

import { useCrudEditContext } from "../../contexts";
import { useRidesNavigation } from "../../navigation";
import { useNewRideTemplate } from "../rides";
import { useDynamicFormsListActions } from "../use-dynamic-forms-list-actions";

export const useJourneysEdit = () => {
  const {
    id: journeyIdFromForm,
    data: { dto, fetchDto },
  } = useCrudEditContext<IExtendableDto>();
  const ridesJourney = useRecoilValue(ridesJourneyAF(journeyIdFromForm || 0));
  const { navigateToRideSetDatesAndIdsFilters } = useRidesNavigation();
  const { openRideEditFormDrawer, openDrawer, closeDrawer } = useDrawers();
  const { getNewRideTemplate } = useNewRideTemplate();
  const setRideDtoGlobal = useSetRecoilState<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const { mergeDataTableRow } = useDynamicFormsListActions("journey");
  const rideFormContext = useRecoilValue(
    hookFormContextAF(RideCrudModeKey.uiUnit),
  );
  const [, startTransition] = useTransition();
  const { timezone } = useTimezone();

  const refetchJourney = useEvent((journeyId: number): void => {
    if (journeyId) {
      startTransition(() => {
        (async () => {
          const response = await fetchDto?.(journeyId);

          if (response?.success && response?.data) {
            mergeDataTableRow(response.data as IExtendableDto);
          }
        })();
      });
    }
  });

  const handleRideFormOpen = useEvent(
    async (
      rideDto: Partial<IRideDto>,
      openDrawerOptions: {
        id?: number;
        idCopyFrom?: number;
      },
    ) => {
      if (rideDto) {
        const { id, idCopyFrom } = openDrawerOptions || {};
        // The ride search doesn't return journeyName, so the ride card would show
        // "No Name". Fill it from the journey being edited (its name is dto.name).
        const rideDtoWithJourneyName: Partial<IRideDto> = {
          ...rideDto,
          journeyName: rideDto.journeyName ?? dto?.name,
        };
        setRideDtoGlobal(rideDtoWithJourneyName);
        rideFormContext?.reset(rideDtoWithJourneyName);
        openRideEditFormDrawer({
          id,
          idCopyFrom,
          onCloseAction: () => {
            refetchJourney(journeyIdFromForm || 0);
          },
        });
      }
    },
  );

  const onRidesNavigationClick = useEvent((): void => {
    if (ridesJourney?.length) {
      const { minDate, maxDate } = getRidesMinFirstMaxLastDate(ridesJourney);

      if (minDate && maxDate) {
        closeDrawer(DrawerType.JourneyEditForm);

        navigateToRideSetDatesAndIdsFilters({
          rideIds: dto?.rideIds || [],
          date1: dayjs
            .utc(minDate)
            .tz(timezone)
            .startOf("day")
            .utc()
            .toISOString(),
          date2: dayjs
            .utc(maxDate)
            .tz(timezone)
            .endOf("day")
            .utc()
            .toISOString(),
        });
      }
    }
  });

  const onAddRideClick = useEvent(async () => {
    let rideFrom: Partial<IRideDto> | undefined = ridesJourney?.[0];

    if (!rideFrom) {
      rideFrom = await getNewRideTemplate({ isJourney: true });
    }

    if (rideFrom) {
      const titleComputed = rideFrom?.metadata?.title
        ? `${rideFrom?.metadata?.title} (copy)`
        : undefined;

      const rideAddDto: Partial<IRideDto> = {
        ...rideFrom,
        id: undefined,
        additionIds: [],
        journeyId: journeyIdFromForm,
        metadata: {
          ...rideFrom.metadata,
          invoice: undefined,
          title: titleComputed,
          duplicationsNumber: undefined,
        },
      };

      handleRideFormOpen(rideAddDto, {
        id: undefined,
        idCopyFrom: rideFrom?.id,
      });
    }
  });

  const onEditRideJourneyClick = useEvent(async (rideId: number) => {
    const rideDto = ridesJourney?.find(ride => ride.id === rideId);
    if (rideDto) {
      handleRideFormOpen(rideDto, {
        id: rideId,
      });
    }
  });

  const onJourneyDuplicateClick = useEvent(() => {
    openDrawer({
      drawerProps: {
        type: DrawerType.JourneyDuplicate,
        assignRides: ridesJourney,
        dto,
      },
    });
  });

  return {
    ridesJourney,
    refetchJourney,
    onAddRideClick,
    onRidesNavigationClick,
    onEditRideJourneyClick,
    onJourneyDuplicateClick,
  };
};
